import type { TFunction } from 'i18next';

/**
 * Returns true when a value should be shown as "not available" in read-only UI.
 * Preserves numeric 0 and boolean false as real values.
 */
export function isEmptyDisplayValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return true;
    }
    if (trimmed === '-') {
      return true;
    }
    const lower = trimmed.toLowerCase();
    if (lower === 'null' || lower === 'undefined') {
      return true;
    }
    return false;
  }
  if (typeof value === 'number') {
    return Number.isNaN(value);
  }
  if (typeof value === 'boolean') {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false;
}

/** Omit employment summary lines whose value is only a placeholder (no "Notice period: N/A months"). */
export function hasMeaningfulSummaryValue(
  value: unknown,
  t: TFunction,
): boolean {
  if (
    typeof value !== 'string' &&
    typeof value !== 'number' &&
    typeof value !== 'boolean'
  ) {
    return false;
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return false;
  }
  if (isEmptyDisplayValue(value)) {
    return false;
  }
  const na = t('N_A');
  if (trimmed === na || trimmed === 'N/A') {
    return false;
  }
  return true;
}

/**
 * Plain-text display helper: missing or placeholder values become the translated N/A label.
 * Use only for user-visible read-only strings, not form payloads.
 */
export function displayValueOrNA(value: unknown, t: TFunction): string {
  if (isEmptyDisplayValue(value)) {
    return t('N_A');
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return t('N_A');
}
